import {
  createCollabDocsScopeLifecycle,
  getCollabDocsSession,
  type CollabDocsScopeLifecycle,
} from '@nimbalyst/collab-bundle/docs-ui';
import { onTeamPageLinksChanged } from '@nimbalyst/collab-bundle/pages';
import type { WebCanvasCardScope } from '../canvas/webCanvasCardScope';
import type { TeamMember, TeamSummary } from '../api';
import type { TeamJwt, TeamSession } from '../auth/teamJwt';
import { orgDocumentPath, orgDocsPath, orgPageItemPath, orgTypePagePath } from '../routing';
import { registerPinnedBrowserExtensions } from '../extensions/pinnedBrowserExtensions';
import { loadOrgRoster } from './orgRoster';
import { onWebDocumentTypesChanged, webDocumentTypeDescriptors } from './webDocumentTypeCatalog';
import { WebCollabDocumentsDataSource } from './WebCollabDocumentsDataSource';
import { createWebPageMarksSource } from './webPageMarks';
import { createWebPageLinksSource, type WebPageLinksSource, type WebPageLinksSourceOptions } from './webPageLinks';
import { openWebDocumentHistory, pageHistoryDocumentId } from './webDocumentHistory';

type DocsHost = Parameters<typeof createCollabDocsScopeLifecycle>[0];
type CollabScope = Awaited<ReturnType<DocsHost['resolveScope']>>;
type ArtifactRef = Parameters<DocsHost['openArtifact']>[0];
type OpenSource = Parameters<DocsHost['openArtifact']>[1];
type TeamMemberSummary = Awaited<ReturnType<DocsHost['getMembers']>>[number];

/**
 * The scope key the docs-ui session is registered under. It is also the key the
 * shared `generateScopeAccentColor` hashes, so anything that paints the project
 * accent (the bar above the tree, the switcher swatches) must derive its color
 * from this exact string or the two disagree.
 */
export function webCollabScopeKey(orgId: string, teamProjectId: string): string {
  return `web-console:${orgId}:${teamProjectId}`;
}

export interface WebCollabHostOptions {
  team: TeamSummary;
  projectId: string;
  teamSession: TeamSession;
  getTeamJwt: (options?: { forceRefresh?: boolean }) => Promise<TeamJwt>;
  serverUrl: string;
  navigate: (path: string) => void;
  reportError?: (error: unknown, context: string) => void;
  createDataSource?: (options: ConstructorParameters<typeof WebCollabDocumentsDataSource>[0]) => WebCollabDocumentsDataSource;
}

/**
 * A page stores its bare name: no ".md" on markdown, as the desktop does.
 * Other document types keep their extension, which names their editor.
 */
export function documentTitle(requestedName: string, documentType: string, defaultExtension: string): string {
  const trimmed = requestedName.trim() || 'Untitled';
  const hasExtension = trimmed.toLowerCase().endsWith(defaultExtension.toLowerCase());
  if (documentType === 'markdown') {
    return (hasExtension ? trimmed.slice(0, trimmed.length - defaultExtension.length).trim() : trimmed) || 'Untitled';
  }
  return hasExtension ? trimmed : `${trimmed}${defaultExtension}`;
}

function mapMember(member: TeamMember): TeamMemberSummary {
  return {
    memberId: member.memberId as TeamMemberSummary['memberId'],
    email: member.email,
    name: member.name,
    role: member.role,
  };
}

/** Browser host for the package-owned scope/session lifecycle. */
export class WebCollabHost implements DocsHost {
  readonly surface = 'web_console' as const;
  readonly personalState = { status: 'unavailable' as const };
  readonly documents: DocsHost['documents'];
  private readonly scope: CollabScope;
  private readonly dataSource: WebCollabDocumentsDataSource;
  private readonly scopeListeners = new Set<(scope: CollabScope | null) => void>();

  constructor(private readonly options: WebCollabHostOptions) {
    // Declares the pinned extensions' manifests only — no bundle is imported
    // here. Without it the catalog below is markdown and nothing else, and
    // every shared spreadsheet resolves to "unsupported".
    registerPinnedBrowserExtensions();
    const teamProjectId = options.projectId;
    this.scope = {
      scopeKey: webCollabScopeKey(options.team.orgId, teamProjectId),
      orgId: options.team.orgId,
      indexConfig: {
        serverUrl: options.serverUrl,
        teamProjectId,
        teamMemberId: options.teamSession.memberId,
      },
    };
    this.dataSource = (options.createDataSource ?? ((dataSourceOptions) => (
      new WebCollabDocumentsDataSource(dataSourceOptions)
    )))({
      orgId: options.team.orgId,
      teamProjectId,
      serverUrl: options.serverUrl,
      getTeamJwt: options.getTeamJwt,
    });
    this.documents = {
      dataSource: this.dataSource,
      loadViewPreferences: async () => null,
      saveViewPreferences: async () => undefined,
      documentTypes: () => webDocumentTypeDescriptors(),
      onDocumentTypesChanged: (cb) => onWebDocumentTypesChanged(cb),
      createDocument: async (input) => {
        const session = getCollabDocsSession(input.scope.scopeKey);
        if (!session) throw new Error('The Shared Docs session is not active');
        const documentId = crypto.randomUUID();
        const title = documentTitle(input.requestedName, input.descriptor.documentType, input.descriptor.defaultExtension);
        await session.registerDocument({
          documentId,
          title,
          documentType: input.descriptor.documentType,
          parentFolderId: input.parentFolderId,
          metadata: {
            metadataVersion: 2,
            fileExtension: input.descriptor.defaultExtension,
            editorId: input.descriptor.editor.kind === 'lexical'
              ? 'builtin.lexical'
              : input.descriptor.editor.kind === 'monaco'
                ? 'builtin.monaco'
                : input.descriptor.editor.extensionId ?? 'unavailable',
          },
        });
        // The new document opens where the reader is, the same as clicking it
        // in the list would. It used to reserve a native tab before the await;
        // see `openArtifact` for why the console no longer opens tabs itself.
        this.options.navigate(orgDocumentPath(this.options.team, input.scope.indexConfig.teamProjectId ?? this.options.projectId, documentId));
      },
      readReceipts: { status: 'unavailable' },
    };
  }

  async resolveScope(): Promise<CollabScope> {
    return this.scope;
  }

  /** The cross-page marks list for this team, over this host's TeamRoom socket. */
  pageMarksSource(): ReturnType<typeof createWebPageMarksSource> {
    return createWebPageMarksSource({
      orgId: this.scope.orgId,
      query: (filters) => this.dataSource.queryPageMarks({ ...filters, projectId: this.scope.indexConfig.teamProjectId ?? undefined }),
    });
  }

  /**
   * A typed page's body links from the team's page links index, over this
   * host's TeamRoom socket. Asks again when the server says links changed or
   * the socket reopens.
   */
  pageLinksSource(resolveItem: WebPageLinksSourceOptions['resolveItem']): WebPageLinksSource {
    return createWebPageLinksSource({
      projectId: this.options.projectId,
      query: (filters) => this.dataSource.queryPageLinks(filters),
      resolveItem,
      onChanged: onTeamPageLinksChanged,
    });
  }

  /**
   * The org, project and credentials a canvas card resolves against.
   *
   * A canvas node names an org and a document id and never a project, and the
   * board renders in its own React root where no context reaches it -- so the
   * project has to come from the one scope this console has open, which is this
   * host. Published by `useProjectDocsScope` alongside `start()`/`dispose()`.
   */
  canvasCardScope(): WebCanvasCardScope {
    return {
      orgId: this.scope.orgId,
      projectId: this.options.projectId,
      teamSession: this.options.teamSession,
      getTeamJwt: this.options.getTeamJwt,
    };
  }

  onScopeChanged(cb: (scope: CollabScope | null) => void): () => void {
    this.scopeListeners.add(cb);
    return () => this.scopeListeners.delete(cb);
  }

  getTeamJwt(orgId: string): ReturnType<DocsHost['getTeamJwt']> {
    if (orgId !== this.scope.orgId) {
      return Promise.reject(new Error('Requested a team JWT for a different organization'));
    }
    // The editor and docs-ui declarations are generated as separate public
    // entries, so their private unique-symbol brands are not nominally equal.
    // The token was already claim-checked and branded by the browser auth seam.
    return this.options.getTeamJwt() as unknown as ReturnType<DocsHost['getTeamJwt']>;
  }

  async getMembers(orgId: string): Promise<TeamMemberSummary[]> {
    if (orgId !== this.scope.orgId) throw new Error('Requested members for a different organization');
    return (await loadOrgRoster(orgId, this.options.getTeamJwt)).map(mapMember);
  }

  /** The console route an artifact opens on; a folder is a page. */
  private artifactPath(ref: ArtifactRef): string {
    const { team, projectId } = this.options;
    switch (ref.kind) {
      case 'document': return orgDocumentPath(team, ref.teamProjectId ?? projectId, ref.documentId);
      case 'folder': return orgDocumentPath(team, projectId, ref.folderId);
      case 'tracker': return orgPageItemPath(team, projectId, ref.trackerId);
      case 'type': return orgTypePagePath(team, projectId, ref.typeId);
    }
  }

  /**
   * Every artifact is a navigation of the current tab.
   *
   * Documents used to open with `window.open(..., '_blank')`. A second browser
   * tab is still one modified click away on any document link, but the console
   * must never force one: inside Nimbalyst's own browser every popup is handed
   * to the system browser, so each document click left the app entirely. The
   * open options (Cmd/Ctrl for a new tab) are for the desktop's tab strip.
   */
  openArtifact(ref: ArtifactRef, source: OpenSource, _options?: Parameters<DocsHost['openArtifact']>[2]): void {
    this.options.navigate(this.artifactPath(ref));
    // View History opens the page and its history together; the page's editor
    // mount shows the dialog once it has a controller.
    if (source !== 'history') return;
    if (ref.kind === 'document') openWebDocumentHistory(ref.documentId);
    // A typed page's body and a type page's prose are rooms of their own.
    else if (ref.kind === 'tracker' || ref.kind === 'type') {
      const historyId = pageHistoryDocumentId({ itemId: ref.kind === 'tracker' ? ref.trackerId : null, typeId: ref.kind === 'type' ? ref.typeId : null });
      if (historyId) openWebDocumentHistory(historyId);
    }
  }

  artifactUrl(ref: ArtifactRef): string | null {
    return new URL(this.artifactPath(ref), window.location.origin).href;
  }

  reportError(error: unknown, context: string): void {
    this.options.reportError?.(error, context);
    console.error(`[WebCollabHost] ${context}`, error);
  }
}

export function createWebCollabLifecycle(
  host: WebCollabHost,
  options: Parameters<typeof createCollabDocsScopeLifecycle>[1],
): CollabDocsScopeLifecycle {
  return createCollabDocsScopeLifecycle(host, options);
}

export function webCollabHomePath(team: TeamSummary, projectId: string): string {
  return orgDocsPath(team, projectId);
}
